/**
 * 送信元メールアカウント一覧セクション（Phase 26）。
 * GmailAuthSection の後継：複数アドレス対応 + Gmail OAuth + SMTP の統合管理。
 */
import {
  AlertCircle,
  CheckCircle2,
  Mail,
  Plus,
  RotateCcw,
  Send,
  Star,
  StarOff,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeleteMailAccount,
  useMailAccounts,
  useRevokeGmailAuth,
  useSetDefaultMailAccount,
  useStartGmailOAuth,
  useTestMailAccount,
  type MailAccountRecord,
} from '@/hooks/useMailAccounts';

import { AddAccountDialog } from './AddAccountDialog';
import { TestSendDialog } from './TestSendDialog';

export function MailAccountsSection() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data: accounts, isLoading, refetch } = useMailAccounts();

  const [addOpen, setAddOpen] = useState(false);
  const [testTarget, setTestTarget] = useState<MailAccountRecord | null>(null);

  const setDefaultMutation = useSetDefaultMailAccount();
  const deleteMutation = useDeleteMailAccount();
  const revokeMutation = useRevokeGmailAuth();
  const startOAuth = useStartGmailOAuth();
  const testMutation = useTestMailAccount();

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultMutation.mutateAsync(id);
      toast.success('既定アカウントを変更しました');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDelete = async (account: MailAccountRecord) => {
    if (
      !window.confirm(
        `送信元 "${account.email}" を削除します。よろしいですか？\n（このアカウントを使用中の進行予約があれば削除できません）`,
      )
    ) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(account.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleReauthGmail = async (account: MailAccountRecord) => {
    try {
      const res = await startOAuth.mutateAsync({ accountId: account.id });
      const popup = window.open(res.authUrl, 'gmail-oauth', 'width=500,height=700');
      if (!popup) window.location.href = res.authUrl;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleRevokeGmail = async (account: MailAccountRecord) => {
    if (!window.confirm(`Gmail 認証 (${account.email}) を解除します。よろしいですか？`)) {
      return;
    }
    try {
      await revokeMutation.mutateAsync(account.id);
      toast.success('解除しました');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-[var(--border)]">
        <div>
          <h3 className="text-base font-bold">送信元メールアドレス</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            配信予約や即時送信で使うアドレスを登録します。複数登録可能で、配信予約フォームでは既定が自動選択されます。
          </p>
        </div>
        {isOwner && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setAddOpen(true)}
            disabled={isLoading}
          >
            <Plus className="size-4" />
            アカウントを追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-6 text-xs text-[var(--text-muted)]">
          <Spinner className="size-4 mr-2" />
          読み込み中...
        </div>
      ) : !accounts || accounts.length === 0 ? (
        <div className="rounded border border-dashed border-[var(--border)] bg-[var(--bg-surface)] p-6 text-center">
          <Mail className="size-8 mx-auto text-[var(--text-muted)] mb-2" />
          <p className="text-sm text-[var(--text-secondary)] mb-1">
            送信元アドレスが未登録です
          </p>
          <p className="text-xs text-[var(--text-muted)]">
            配信予約・即時送信を使うには 1 件以上のアカウントを追加してください
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((acc) => (
            <AccountCard
              key={acc.id}
              account={acc}
              isOwner={isOwner}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              onReauthGmail={handleReauthGmail}
              onRevokeGmail={handleRevokeGmail}
              onTest={(a) => setTestTarget(a)}
              busy={
                setDefaultMutation.isPending ||
                deleteMutation.isPending ||
                revokeMutation.isPending ||
                startOAuth.isPending ||
                testMutation.isPending
              }
            />
          ))}
        </div>
      )}

      <AddAccountDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => {
          void refetch();
        }}
      />

      <TestSendDialog
        account={testTarget}
        onOpenChange={(open) => {
          if (!open) setTestTarget(null);
        }}
      />
    </div>
  );
}

interface AccountCardProps {
  account: MailAccountRecord;
  isOwner: boolean;
  busy: boolean;
  onSetDefault: (id: string) => void;
  onDelete: (account: MailAccountRecord) => void;
  onReauthGmail: (account: MailAccountRecord) => void;
  onRevokeGmail: (account: MailAccountRecord) => void;
  onTest: (account: MailAccountRecord) => void;
}

function AccountCard({
  account,
  isOwner,
  busy,
  onSetDefault,
  onDelete,
  onReauthGmail,
  onRevokeGmail,
  onTest,
}: AccountCardProps) {
  return (
    <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] p-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          {account.status === 'active' ? (
            <CheckCircle2 className="size-5 text-[var(--accent-green)]" />
          ) : (
            <AlertCircle className="size-5 text-[var(--accent-red)]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold truncate">{account.displayName}</span>
            {account.isDefault && (
              <Badge variant="approved">
                <Star className="size-3" />
                既定
              </Badge>
            )}
            <Badge variant={account.provider === 'gmail_oauth' ? 'accent' : 'default'}>
              {account.provider === 'gmail_oauth' ? 'Gmail' : 'SMTP'}
            </Badge>
            <StatusBadge status={account.status} />
          </div>
          <div className="text-xs font-mono text-[var(--text-secondary)] mt-1 truncate">
            {account.email || '(認証フロー中…)'}
          </div>
          {account.lastError && (
            <div className="text-[11px] text-[var(--accent-red)] mt-1 line-clamp-2">
              直近エラー: {account.lastError}
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="flex items-center justify-end gap-1.5 mt-3 flex-wrap">
          {!account.isDefault && account.status === 'active' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onSetDefault(account.id)}
              disabled={busy}
            >
              <StarOff className="size-3.5" />
              既定にする
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onTest(account)}
            disabled={busy || account.status !== 'active'}
          >
            <Send className="size-3.5" />
            テスト送信
          </Button>
          {account.provider === 'gmail_oauth' ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onReauthGmail(account)}
                disabled={busy}
              >
                <RotateCcw className="size-3.5" />
                再認証
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRevokeGmail(account)}
                disabled={busy || account.status === 'revoked'}
              >
                解除
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onDelete(account)}
            disabled={busy}
            className="text-[var(--accent-red)]"
          >
            <Trash2 className="size-3.5" />
            削除
          </Button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: MailAccountRecord['status'] }) {
  switch (status) {
    case 'active':
      return <Badge variant="approved">利用可</Badge>;
    case 'pending_oauth':
      return <Badge variant="draft">認証フロー中</Badge>;
    case 'unauthorized':
      return <Badge variant="danger">要再認証</Badge>;
    case 'revoked':
      return <Badge variant="default">解除済</Badge>;
    default:
      return null;
  }
}
